// Salespersons Page — List, Create, Edit, Delete, Restore

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Button, Alert, PageHeader } from '../components/common/index.js';
import {
  fetchSalespersons,
  fetchDeletedSalespersons,
  createSalesperson,
  updateSalesperson,
  deleteSalesperson,
  restoreSalesperson,
  toggleSalespersonActive,
  clearError,
} from '../redux/slices/salespersonSlice';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiX,
  FiUser,
  FiPhone,
  FiMail,
  FiAlertTriangle,
  FiUsers,
  FiEye,
  FiEyeOff,
  FiUserCheck,
  FiUserX,
  FiRefreshCw,
  FiClipboard,
  FiBarChart2,
  FiTrendingUp,
  FiCalendar,
  FiXCircle,
  FiCreditCard,
  FiDownload,
} from 'react-icons/fi';
import { downloadSalespersonsWorkbook } from '../utils/teamExcel';

// Compact INR — ₹1.2 L / ₹3.4 Cr for big turnover numbers, plain below a lakh.
const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '₹0';
  const a = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (a >= 10000000) return `${sign}₹${(a / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `${sign}₹${(a / 100000).toFixed(2)} L`;
  return `${sign}₹${a.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

// ─── Avatar colour pool ───────────────────────────────────────────────────────
const GRADIENTS = [
  'from-sky-500 to-cyan-600',
  'from-violet-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-500',
  'from-fuchsia-500 to-purple-600',
];
const grad = (i) => GRADIENTS[i % GRADIENTS.length];

// ─── Active Toggle ────────────────────────────────────────────────────────────
const ActiveToggle = ({ active, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 ${
      active ? 'bg-emerald-500' : 'bg-slate-300'
    }`}
    role="switch"
    aria-checked={active}
  >
    <span className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${active ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);

// ─── Modal Shell ──────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-fade-in-up max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

// ─── Form Field ───────────────────────────────────────────────────────────────
const Field = ({ label, icon: Icon, error, children }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />}
      {React.cloneElement(children, {
        className: `w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 border rounded-xl text-sm text-slate-800 placeholder-slate-400 transition-all focus:outline-none focus:ring-2 ${
          error
            ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
            : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
        }`,
      })}
    </div>
    {error && <p className="text-rose-500 text-xs mt-1 font-medium">{error}</p>}
  </div>
);

// ─── Salesperson Form ─────────────────────────────────────────────────────────
const EMPTY_FORM = { name: '', email: '', phone_number: '', password: '' };

const SalespersonForm = ({ initial = EMPTY_FORM, isEdit = false, submitting, apiError, onSubmit, onClose }) => {
  const [form, setForm]         = useState(initial);
  const [errors, setErrors]     = useState({});
  const [showPass, setShowPass] = useState(false);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handlePhone = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm((p) => ({ ...p, phone_number: digits }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.phone_number.trim()) {
      e.phone_number = 'Phone number is required';
    } else if (form.phone_number.length !== 10) {
      e.phone_number = 'Enter exactly 10 digits';
    }
    if (!isEdit && !form.password.trim()) e.password = 'Password is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = { name: form.name, phone_number: `+91${form.phone_number}` };
    // Email is ALWAYS sent, null when the box is empty. A PATCH treats a
    // missing key as "leave it alone" — so the old `if (email) payload.email`
    // made clearing the field impossible: the admin emptied the box, saved,
    // and the server was never told. An empty box is an instruction.
    payload.email = form.email.trim() ? form.email.trim() : null;
    // Password is the one field where omission IS correct: an empty box means
    // "keep the current password", not "remove it".
    if (form.password.trim()) payload.password = form.password;
    onSubmit(payload);
  };

  const apiErrorMsg = apiError
    ? typeof apiError === 'string'
      ? apiError
      : Object.entries(apiError).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ')
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {apiErrorMsg && <Alert type="error" message={apiErrorMsg} />}

      <Field label="Full Name *" icon={FiUser} error={errors.name}>
        <input type="text" placeholder="e.g. Rajesh Kumar" value={form.name} onChange={set('name')} disabled={submitting} />
      </Field>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number *</label>
        <div className={`flex rounded-xl overflow-hidden border transition-all focus-within:ring-2 ${
          errors.phone_number ? 'border-rose-400 focus-within:ring-rose-300' : 'border-slate-200 focus-within:ring-violet-300 focus-within:border-violet-400'
        }`}>
          <span className="flex items-center gap-1.5 px-3 bg-slate-100 text-slate-500 text-sm font-semibold border-r border-slate-200 shrink-0 select-none">
            <FiPhone className="w-3.5 h-3.5" /> +91
          </span>
          <input
            type="tel" inputMode="numeric" placeholder="98765 43210"
            value={form.phone_number} onChange={handlePhone} disabled={submitting} maxLength={10}
            className="flex-1 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none"
          />
          <span className={`flex items-center px-3 text-xs font-medium ${form.phone_number.length === 10 ? 'text-emerald-600' : 'text-slate-400'}`}>
            {form.phone_number.length}/10
          </span>
        </div>
        {errors.phone_number && <p className="text-rose-500 text-xs mt-1 font-medium">{errors.phone_number}</p>}
      </div>

      <Field label="Email" icon={FiMail} error={errors.email}>
        <input type="email" placeholder="salesperson@cap.com (optional)" value={form.email} onChange={set('email')} disabled={submitting} />
      </Field>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          {isEdit ? 'New Password' : 'Password *'}
        </label>
        <div className={`flex rounded-xl overflow-hidden border transition-all focus-within:ring-2 ${
          errors.password ? 'border-rose-400 focus-within:ring-rose-300' : 'border-slate-200 focus-within:ring-violet-300 focus-within:border-violet-400'
        }`}>
          <input
            type={showPass ? 'text' : 'password'}
            placeholder={isEdit ? 'Leave blank to keep current' : 'Set a password'}
            value={form.password} onChange={set('password')} disabled={submitting}
            className="flex-1 px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none"
          />
          <button type="button" onClick={() => setShowPass((v) => !v)} disabled={submitting}
            className="px-3 text-slate-400 hover:text-slate-700 bg-white transition-colors" tabIndex={-1}>
            {showPass ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
          </button>
        </div>
        {errors.password && <p className="text-rose-500 text-xs mt-1 font-medium">{errors.password}</p>}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="primary" size="md" loading={submitting}>
          {isEdit ? 'Save Changes' : 'Add Salesperson'}
        </Button>
      </div>
    </form>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteConfirm = ({ salesperson, submitting, apiError, onConfirm, onClose }) => {
  const [adminPass, setAdminPass] = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [passError, setPassError] = useState('');

  const handleConfirm = () => {
    if (!adminPass.trim()) { setPassError('Admin password is required'); return; }
    setPassError('');
    onConfirm(adminPass);
  };

  const errMsg = apiError ? (typeof apiError === 'string' ? apiError : 'Delete failed. Check your password.') : null;

  return (
    <Modal title="Delete Salesperson" onClose={onClose}>
      <div className="flex flex-col items-center text-center gap-4">
        <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
          <FiAlertTriangle className="w-7 h-7 text-rose-500" />
        </div>
        <div>
          <p className="font-semibold text-slate-800 mb-1">
            Remove <span className="text-rose-600">{salesperson.name}</span>?
          </p>
          <p className="text-slate-500 text-sm">
            This is a soft delete — the record can be restored later. Enter your admin password to confirm.
          </p>
        </div>
        {errMsg && <Alert type="error" message={errMsg} className="w-full text-left" />}
        <div className="w-full text-left">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Admin Password</label>
          <div className={`flex rounded-xl overflow-hidden border transition-all focus-within:ring-2 ${
            passError ? 'border-rose-400 focus-within:ring-rose-300' : 'border-slate-200 focus-within:ring-violet-300 focus-within:border-violet-400'
          }`}>
            <input
              type={showPass ? 'text' : 'password'}
              placeholder="Enter your admin password"
              value={adminPass}
              onChange={(e) => { setAdminPass(e.target.value); setPassError(''); }}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none"
            />
            <button type="button" onClick={() => setShowPass((v) => !v)} disabled={submitting}
              className="px-3 text-slate-400 hover:text-slate-700 bg-white transition-colors" tabIndex={-1}>
              {showPass ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
            </button>
          </div>
          {passError && <p className="text-rose-500 text-xs mt-1 font-medium">{passError}</p>}
        </div>
        <div className="flex gap-3 w-full">
          <Button variant="secondary" size="md" onClick={onClose} disabled={submitting} className="flex-1">Cancel</Button>
          <Button variant="danger" size="md" onClick={handleConfirm} loading={submitting} className="flex-1">Yes, Delete</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Inquiry Stats Badge ──────────────────────────────────────────────────────
const InquiryBadge = ({ stats }) => {
  if (!stats) return null;
  const total = stats.total ?? 0;
  return (
    <div className="flex items-center gap-1 px-2 py-0.5 bg-violet-50 border border-violet-100 rounded-full shrink-0">
      <FiClipboard className="w-2.5 h-2.5 text-violet-400" />
      <span className="text-xs font-semibold text-violet-600">{total}</span>
    </div>
  );
};

// ─── Stat Chip ────────────────────────────────────────────────────────────────
const StatChip = ({ icon: Icon, iconBg, iconColor, label, value, valueColor }) => (
  <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex-1 min-w-[90px]">
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-400 leading-none mb-0.5">{label}</p>
      <p className={`text-lg font-bold leading-none ${valueColor}`}>{value}</p>
    </div>
  </div>
);

// ─── Spinner / Empty State ────────────────────────────────────────────────────
const Spinner = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="animate-spin w-8 h-8 text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  </div>
);

const EmptyState = ({ isDeleted, search }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-4">
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isDeleted ? 'bg-rose-50' : 'bg-violet-50'}`}>
      {isDeleted
        ? <FiTrash2 className="w-7 h-7 text-rose-300" />
        : <FiUsers className="w-7 h-7 text-violet-400" />}
    </div>
    <p className="text-slate-700 font-semibold mb-1">
      {search ? 'No results found' : isDeleted ? 'No deleted records' : 'No salespersons yet'}
    </p>
    <p className="text-slate-400 text-sm">
      {search ? 'Try a different search term.' : isDeleted ? 'Deleted salespersons will appear here.' : 'Add your first salesperson to get started.'}
    </p>
  </div>
);

// ─── Active Salesperson Card (mobile/tablet) ──────────────────────────────────
const SalespersonCard = ({ s, index, onEdit, onDelete, onToggle, onAnalytics }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in-up">
    <div className={`h-1 w-full bg-gradient-to-r ${grad(index)}`} />
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${grad(index)} flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm`}>
          {s.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-slate-900 truncate flex-1">{s.name}</p>
            <InquiryBadge stats={s.inquiry_stats} />
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <FiPhone className="w-3 h-3 text-slate-400 shrink-0" />
            <p className="text-xs text-slate-500">{s.phone_number || '—'}</p>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <FiMail className="w-3 h-3 text-slate-400 shrink-0" />
            <p className="text-xs text-slate-400 truncate">
              {s.email || <span className="text-slate-300 italic">No email</span>}
            </p>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <FiTrendingUp className="w-3 h-3 text-indigo-400 shrink-0" />
            <p className="text-xs font-bold text-indigo-600">{fmtMoney(s.turnover)}</p>
            <span className="text-[11px] text-slate-400">· {s.deals ?? 0} {Number(s.deals) === 1 ? 'deal' : 'deals'}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">{s.sold ?? 0} sold</span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 bg-rose-50 text-rose-500 rounded-full">{s.lost ?? 0} lost</span>
            {Number(s.balance_due) > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full">
                {fmtMoney(s.balance_due)} due
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-slate-100 mt-3 pt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ActiveToggle active={s.is_active} onChange={onToggle} />
          <span className={`text-xs font-semibold ${s.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
            {s.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onAnalytics} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all" title="Analytics">
            <FiBarChart2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onEdit} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-all" title="Edit">
            <FiEdit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all" title="Delete">
            <FiTrash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── Deleted Salesperson Card (mobile/tablet) ─────────────────────────────────
const DeletedCard = ({ s, index, onRestore, submitting }) => (
  <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden animate-fade-in-up opacity-80">
    <div className="h-1 w-full bg-gradient-to-r from-rose-300 to-rose-400" />
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-slate-200 flex items-center justify-center text-slate-400 text-sm font-bold shrink-0">
          {s.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-slate-500 truncate flex-1 line-through decoration-rose-300">{s.name}</p>
            <span className="text-[10px] font-bold text-rose-500 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full shrink-0">Deleted</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <FiPhone className="w-3 h-3 text-slate-300 shrink-0" />
            <p className="text-xs text-slate-400">{s.phone_number || '—'}</p>
          </div>
          {s.deleted_at && (
            <p className="text-xs text-slate-300 mt-0.5">Deleted {s.deleted_at}</p>
          )}
        </div>
      </div>
      <div className="border-t border-slate-100 mt-3 pt-3 flex items-center justify-end">
        <button
          onClick={onRestore}
          disabled={submitting}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-lg hover:bg-emerald-100 transition-all disabled:opacity-50"
        >
          <FiRefreshCw className={`w-3 h-3 ${submitting ? 'animate-spin' : ''}`} />
          Restore
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',      label: 'All' },
  { id: 'active',   label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'deleted',  label: 'Deleted', danger: true },
];

const SalespersonsPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const {
    list, deletedList,
    count, deletedCount,
    teamTurnover, teamDeals, teamInquiries, teamLost, teamBalance,
    loading, loadingDeleted, deletedFetched,
    submitting, error,
  } = useSelector((s) => s.salespersons);

  const [search,       setSearch]       = useState('');
  const [activeTab,    setActiveTab]    = useState('all');
  const [showAdd,      setShowAdd]      = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Period filter — narrows every NUMBER on the screen (inquiries, turnover,
  // sold/lost, balance) while the people stay listed. 'custom' opens the two
  // date boxes; `to` is the last day INCLUDED.
  const [period,   setPeriod]   = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  useEffect(() => {
    if (period === 'custom') {
      // Wait until at least one date is picked — firing on an empty custom
      // range would silently re-fetch all-time numbers under a "Custom" label.
      if (!dateFrom && !dateTo) return;
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo)   params.to   = dateTo;
      dispatch(fetchSalespersons(params));
      return;
    }
    dispatch(fetchSalespersons(period === 'all' ? {} : { period }));
  }, [dispatch, period, dateFrom, dateTo]);

  // Lazy-load deleted list only when that tab is first opened
  useEffect(() => {
    if (activeTab === 'deleted' && !deletedFetched) {
      dispatch(fetchDeletedSalespersons());
    }
  }, [activeTab, deletedFetched, dispatch]);

  const closeAdd  = useCallback(() => { setShowAdd(false);     dispatch(clearError()); }, [dispatch]);
  const closeEdit = useCallback(() => { setEditTarget(null);   dispatch(clearError()); }, [dispatch]);
  const closeDel  = useCallback(() => { setDeleteTarget(null); dispatch(clearError()); }, [dispatch]);

  const handleCreate = async (data) => {
    const res = await dispatch(createSalesperson(data));
    if (!res.error) closeAdd();
  };

  const handleUpdate = async (data) => {
    const res = await dispatch(updateSalesperson({ id: editTarget.id, data }));
    if (!res.error) closeEdit();
  };

  const handleDelete = async (password) => {
    const res = await dispatch(deleteSalesperson({ id: deleteTarget.id, password }));
    if (!res.error) closeDel();
  };

  const handleRestore = (id) => dispatch(restoreSalesperson(id));
  const handleToggleActive = (sp) => dispatch(toggleSalespersonActive({ id: sp.id, is_active: !sp.is_active }));
  const handleAnalytics = (id) => navigate(`/salespersons/${id}/analytics`);

  const handleRefresh = () => {
    if (activeTab === 'deleted') {
      dispatch(fetchDeletedSalespersons());
    } else if (period === 'custom') {
      // Refresh must re-ask for the SAME window — a bare fetch here would
      // silently swap the screen (and the Excel) back to all-time numbers
      // while the chips still claim the filter.
      dispatch(fetchSalespersons({
        ...(dateFrom ? { from: dateFrom } : {}),
        ...(dateTo ? { to: dateTo } : {}),
      }));
    } else {
      dispatch(fetchSalespersons(period === 'all' ? {} : { period }));
    }
  };

  // ── Computed lists per tab ──────────────────────────────────────────────────

  const tabFiltered = (() => {
    switch (activeTab) {
      case 'active':   return list.filter((s) => s.is_active);
      case 'inactive': return list.filter((s) => !s.is_active);
      case 'deleted':  return deletedList;
      default:         return list;
    }
  })();

  const q = search.toLowerCase();
  const filtered = tabFiltered.filter((s) =>
    s.name.toLowerCase().includes(q) ||
    (s.phone_number || '').includes(q) ||
    (s.email || '').toLowerCase().includes(q)
  );

  // Tab counts
  const tabCounts = {
    all:      count,
    active:   list.filter((s) => s.is_active).length,
    inactive: list.filter((s) => !s.is_active).length,
    deleted:  deletedFetched ? deletedCount : null,
  };

  const isDeletedTab = activeTab === 'deleted';
  const isLoading    = isDeletedTab ? loadingDeleted : loading;

  const activeCount   = list.filter((s) => s.is_active).length;
  const inactiveCount = list.filter((s) => !s.is_active).length;

  // What the Excel calls the window — always the filter that is actually set.
  const windowLabel = period === 'custom'
    ? `${dateFrom || 'the beginning'} → ${dateTo || 'today'}`
    : { all: 'All time', today: 'Today', week: 'This week', month: 'This month' }[period] || period;

  const downloadExcel = () => downloadSalespersonsWorkbook({
    // The rows the admin is LOOKING at — tab and search included.
    rows: filtered,
    team: { inquiries: teamInquiries, deals: teamDeals, turnover: teamTurnover,
            lost: teamLost, balance: teamBalance },
    windowLabel,
  });

  return (
    <MainLayout>
      <div className="space-y-5">

        <PageHeader meta={`${count} registered`}>
          {!isDeletedTab && (
            <button
              onClick={downloadExcel}
              disabled={isLoading || !filtered.length
                || (period === 'custom' && !dateFrom && !dateTo)}
              title={`Download this list — ${windowLabel}`}
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-all active:scale-95 shrink-0 disabled:opacity-60"
            >
              <FiDownload className="w-4 h-4" /> Excel
            </button>
          )}
          {!isDeletedTab && (
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl shadow-md shadow-violet-500/20 transition-all active:scale-95 shrink-0"
            >
              <FiPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Salesperson</span>
              <span className="sm:hidden">Add</span>
            </button>
          )}
        </PageHeader>

        {/* ── Period filter ───────────────────────────────────────────────── */}
        {/* Everything below follows this window — the counts on each row, the
            turnover, sold/lost and the balance chips. The PEOPLE always stay
            listed; an empty window zeroes their numbers, it never hides them. */}
        {!isDeletedTab && (
          <div className="flex flex-wrap items-center gap-2 animate-fade-in-up" style={{ animationDelay: '0.04s' }}>
            {[
              { id: 'all',    label: 'All time' },
              { id: 'today',  label: 'Today' },
              { id: 'week',   label: 'Week' },
              { id: 'month',  label: 'Month' },
              { id: 'custom', label: 'Date range' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                  period === p.id
                    ? 'border-violet-600 bg-violet-600 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'
                }`}
              >
                {p.id === 'custom' && <FiCalendar className="w-3 h-3" />}
                {p.label}
              </button>
            ))}
            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none" />
                {(dateFrom || dateTo) && (
                  <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-xs font-semibold text-slate-400 hover:text-rose-500 transition">
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <StatChip icon={FiUsers}     iconBg="bg-violet-50"  iconColor="text-violet-500"  label="Total"    value={count}         valueColor="text-slate-800" />
          <StatChip icon={FiUserCheck} iconBg="bg-emerald-50" iconColor="text-emerald-500" label="Active"   value={activeCount}   valueColor="text-emerald-600" />
          <StatChip icon={FiUserX}     iconBg="bg-slate-100"  iconColor="text-slate-400"   label="Inactive" value={inactiveCount} valueColor="text-slate-500" />
          {!isDeletedTab && (
            <>
              <StatChip icon={FiClipboard} iconBg="bg-violet-50" iconColor="text-violet-500"
                label="Inquiries" value={teamInquiries ?? 0} valueColor="text-violet-700" />
              <StatChip icon={FiTrendingUp} iconBg="bg-indigo-50" iconColor="text-indigo-500"
                label={`Turnover · ${teamDeals ?? 0} sold`} value={fmtMoney(teamTurnover)} valueColor="text-indigo-700" />
              <StatChip icon={FiCreditCard} iconBg="bg-amber-50" iconColor="text-amber-500"
                label="Balance to collect" value={fmtMoney(teamBalance)} valueColor="text-amber-700" />
              <StatChip icon={FiXCircle} iconBg="bg-rose-50" iconColor="text-rose-400"
                label="Lost" value={teamLost ?? 0} valueColor="text-rose-600" />
            </>
          )}
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit animate-fade-in-up" style={{ animationDelay: '0.07s' }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const cnt = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearch(''); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                  isActive
                    ? tab.danger
                      ? 'bg-rose-500 text-white shadow-sm'
                      : 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                {cnt !== null && cnt !== undefined && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                    isActive
                      ? tab.danger ? 'bg-white/20 text-white' : 'bg-violet-100 text-violet-700'
                      : 'bg-slate-200 text-slate-500'
                  }`}>
                    {cnt}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Search + Refresh ────────────────────────────────────────────── */}
        <div className="flex gap-2 animate-fade-in-up" style={{ animationDelay: '0.09s' }}>
          <div className="relative flex-1">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder={`Search ${isDeletedTab ? 'deleted' : activeTab === 'all' ? 'all' : activeTab} salespersons...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-10 py-3 border border-slate-200 rounded-xl bg-white text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all shadow-sm"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh"
            className="w-12 h-12 shrink-0 flex items-center justify-center bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiRefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* ── Mobile / Tablet: card grid ───────────────────────────────────── */}
        <div className="lg:hidden animate-fade-in-up" style={{ animationDelay: '0.11s' }}>
          {isLoading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
              <EmptyState isDeleted={isDeletedTab} search={search} />
            </div>
          ) : isDeletedTab ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((s, i) => (
                <DeletedCard
                  key={s.id}
                  s={s}
                  index={i}
                  onRestore={() => handleRestore(s.id)}
                  submitting={submitting}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((s, i) => (
                <SalespersonCard
                  key={s.id}
                  s={s}
                  index={i}
                  onAnalytics={() => handleAnalytics(s.id)}
                  onEdit={() => setEditTarget(s)}
                  onDelete={() => setDeleteTarget(s)}
                  onToggle={() => handleToggleActive(s)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Desktop: table ───────────────────────────────────────────────── */}
        <div className="hidden lg:block bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.11s' }}>
          {isLoading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <EmptyState isDeleted={isDeletedTab} search={search} />
          ) : isDeletedTab ? (
            /* Deleted tab table */
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-rose-50/60 border-b border-rose-100">
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5 w-12">#</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Name</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Phone</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Email</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Deleted At</th>
                    <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((s, index) => (
                    <tr key={s.id} className="hover:bg-rose-50/30 transition-colors opacity-75">
                      <td className="px-6 py-4 text-sm font-semibold text-slate-300">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400 text-xs font-bold shrink-0">
                            {s.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-500 line-through decoration-rose-300">{s.name}</p>
                            <span className="text-[10px] font-bold text-rose-500 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full">Deleted</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-400">{s.phone_number || '—'}</td>
                      <td className="px-6 py-4 text-sm text-slate-400">{s.email || <span className="text-slate-300">—</span>}</td>
                      <td className="px-6 py-4 text-xs text-slate-400 whitespace-nowrap">{s.deleted_at || '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleRestore(s.id)}
                          disabled={submitting}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-lg hover:bg-emerald-100 transition-all disabled:opacity-50 ml-auto"
                        >
                          <FiRefreshCw className={`w-3 h-3 ${submitting ? 'animate-spin' : ''}`} />
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Active / All / Inactive table */
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5 w-12">#</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Name</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Phone</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Email</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Inquiries</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Turnover</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Sold / Lost</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Balance</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Status</th>
                    <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((s, index) => (
                    <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-6 py-4 text-sm font-semibold text-slate-300">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${grad(index)} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                            {s.name.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-slate-800">{s.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{s.phone_number || '—'}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">{s.email || <span className="text-slate-300">—</span>}</td>
                      <td className="px-6 py-4">
                        {s.inquiry_stats ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-700">{s.inquiry_stats.total ?? 0}</span>
                            {s.inquiry_stats.new > 0 && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-full">{s.inquiry_stats.new} new</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-bold text-indigo-700 tabular-nums">{fmtMoney(s.turnover)}</p>
                        <p className="text-[11px] text-slate-400">{s.deals ?? 0} {Number(s.deals) === 1 ? 'deal' : 'deals'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">{s.sold ?? 0} sold</span>
                          <span className="text-[11px] font-bold px-1.5 py-0.5 bg-rose-50 text-rose-500 rounded-full">{s.lost ?? 0} lost</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {Number(s.balance_due) > 0 ? (
                          <p className="text-sm font-bold text-amber-600 tabular-nums">{fmtMoney(s.balance_due)}</p>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <ActiveToggle active={s.is_active} onChange={() => handleToggleActive(s)} />
                          <span className={`text-xs font-semibold ${s.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {s.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleAnalytics(s.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all" title="Analytics">
                            <FiBarChart2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setEditTarget(s)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-all" title="Edit">
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setDeleteTarget(s)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all" title="Delete">
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showAdd && (
        <Modal title="Add New Salesperson" onClose={closeAdd}>
          <SalespersonForm submitting={submitting} apiError={error} onSubmit={handleCreate} onClose={closeAdd} />
        </Modal>
      )}

      {editTarget && (
        <Modal title="Edit Salesperson" onClose={closeEdit}>
          <SalespersonForm
            isEdit
            initial={{
              name:         editTarget.name,
              email:        editTarget.email || '',
              phone_number: (editTarget.phone_number || '').replace(/^\+91\s?/, ''),
              password:     '',
            }}
            submitting={submitting}
            apiError={error}
            onSubmit={handleUpdate}
            onClose={closeEdit}
          />
        </Modal>
      )}

      {deleteTarget && (
        <DeleteConfirm
          salesperson={deleteTarget}
          submitting={submitting}
          apiError={error}
          onConfirm={handleDelete}
          onClose={closeDel}
        />
      )}
    </MainLayout>
  );
};

export default SalespersonsPage;
